// src/pages/Home.js
import React, { useState, useEffect, useMemo } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import Card from '../components/Card';
import '../styles/MultiSelect.css';
import '../styles/Home.css';
import JapaneseMenu, { japaneseGrammarItems } from '../menus/JapaneseMenu';
import ItalianMenu from '../menus/ItalianMenu';
import SpanishMenu from '../menus/SpanishMenu';
import FrenchMenu from '../menus/FrenchMenu';
import KoreanMenu, { koreanGrammarItems } from '../menus/KoreanMenu';
import ChineseMenu, { chineseGrammarItems } from '../menus/ChineseMenu';
import tipsOnIcon from '../images/icons/tips-on.svg';
import tipsOffIcon from '../images/icons/tips-off.svg';
import userActivityService from '../services/userActivityService';
import logger from '../utils/logger';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';

// --- Valid verb tense definitions (should match those in your menus) ---
const spanishVerbTensesList = {
    Beginner: [
        "Present (Presente de Indicativo)",
        "Preterite (Pretérito Indefinido)",
        "Imperfect (Pretérito Imperfecto)",
        "Future Simple (Futuro Simple)",
        "Conditional (Condicional Simple)",
        "Imperative (Imperativo)",
        "Present Perfect (Pretérito Perfecto)"
    ],
    Intermediate: [
        "Subjunctive Present (Presente de Subjuntivo)",
        "Past Perfect (Pretérito Pluscuamperfecto)",
        "Future Perfect (Futuro Perfecto)",
        "Conditional Perfect (Condicional Compuesto)",
        "Imperfect Subjunctive (Pretérito Imperfecto de Subjuntivo)"
    ],
    Advanced: [
        "Past Perfect Subjunctive (Pluscuamperfecto de Subjuntivo)",
        "Future Subjunctive (Futuro de Subjuntivo)"
    ]
};

const frenchVerbTensesList = {
    Beginner: ["Présent", "Passé Composé", "Imparfait"],
    Intermediate: ["Futur Simple", "Conditionnel Présent", "Subjonctif Présent"],
    Advanced: ["Plus-que-parfait", "Passé Simple", "Passé Antérieur", "Conditionnel Passé", "Futur Antérieur"]
};

const italianVerbTensesList = {
    Beginner: ["Presente", "Passato Remoto", "Imperfetto", "Futuro Semplice", "Condizionale"],
    Intermediate: ["Congiuntivo Presente", "Trapassato Remoto", "Futuro Anteriore", "Condizionale Passato"],
    Advanced: ["Congiuntivo Imperfetto", "Congiuntivo Trapassato"]
};

// Refactored RecommendationsAccordion as a controlled component.
// This component will be removed from the menu section but we'll keep it for reference
const RecommendationsAccordion = ({ isOpen, onToggle }) => {
    return (
        <div
            className="home-recommendations-container"
            style={{
                width: '250px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                overflow: 'hidden',
                marginTop: '10px',
                boxSizing: 'border-box',
                color: '#333'
            }}
        >
            <div
                className="accordion-header"
                onClick={onToggle}
                style={{
                    backgroundColor: '#fff',
                    padding: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#333'
                }}
            >
                Recommendations:
                <span style={{ color: '#333' }}>{isOpen ? '▲' : '▼'}</span>
            </div>
            <div
                className={`accordion-content ${isOpen ? 'open' : ''}`}
                style={{
                    backgroundColor: '#fff',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    color: '#333',
                    maxHeight: isOpen ? '500px' : '0',
                    opacity: isOpen ? 1 : 0,
                    padding: isOpen ? '10px' : '0 10px',
                    transition: 'max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease'
                }}
            >
                <ul
                    style={{
                        margin: 0,
                        paddingLeft: '20px',
                        listStyleType: 'disc',
                        color: '#333'
                    }}
                >
                    <li>If the sentences load improperly, simply try reloading them again.</li>
                    <li>
                        For better results with studying verbs, it is recommended to choose one verb tense and one verb ending at a time to add to your playlist. Spend time mastering each one before proceeding.
                    </li>
                    <li>
                        For better quality sentences, it is better to choose a few different topics at a time. Recommended: 3 - 4.
                    </li>
                    <li>
                        Feel free to skip adding certain sentences to your playlist if you don't like them, and then reload more to complete your daily playlist. Pro members can load up to 15 times, and Trial users can load up to 5.
                    </li>
                </ul>
            </div>
        </div>
    );
};

// Define shared topics list used across all languages
const topicsListShared = [
    "Relationships",
    "School and Education",
    "Career",
    "Shopping & Money",
    "Health and Wellness",
    "Food & Dining",
    "Travel & Adventure",
    "Weather & Nature",
    "Science",
    "Technology",
    "History",
    "Philosophy & Deep Thoughts",
    "Spirituality and Religion",
    "Movies & TV",
    "Music",
    "Hobbies & Interests",
    "Colors, Numbers, & Shapes",
    "Fun Facts!"
];

// Japanese vocabulary levels
const japaneseVocabularyLevels = ["N5", "N4", "N3", "N2", "N1"];

// Korean vocabulary levels
const koreanVocabularyLevels = ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6"];

// Chinese vocabulary levels
const chineseVocabularyLevels = ["HSK1", "HSK2", "HSK3", "HSK4", "HSK5", "HSK6"];

// Spanish, French, Italian verb endings
const spanishVerbEndingsList = ['-AR', '-ER', '-IR'];
const frenchVerbEndingsList = ['-ER', '-IR', '-RE'];
const italianVerbEndingsList = ['-ARE', '-ERE', '-IRE'];

// The Home component
const Home = ({ isSidebarOpen, refreshSettings, settings: propSettings }) => {
    // Get authentication and user plan from Firebase context
    const { isAuthenticated, getToken, userPlan, planExpiresAt } = useFirebase();
    const [showTips, setShowTips] = useState(false);
    
    // Initialize settings from localStorage or props
    const [settings, setSettings] = useState(() => {
        const storedSettings = localStorage.getItem('homeSettings');
        return propSettings || (storedSettings ? JSON.parse(storedSettings) : {});
    });

    // Check if trial has expired or subscription is canceled
    const isTrialExpired = () => {
        // Always consider canceled plans as expired
        if (userPlan === 'canceled') return true;
        
        // Check if trial is expired by date
        if (userPlan !== 'trial' || !planExpiresAt) return false;
        const now = new Date();
        const expiration = new Date(planExpiresAt);
        return now > expiration;
    };

    // Get appropriate message based on plan status
    const getAccessLimitMessage = () => {
        if (userPlan === 'canceled') {
            return (
                <>
                    Your subscription has been canceled. Please <Link 
                        to="/pricing" 
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to regain access.
                </>
            );
        } else if (userPlan === 'trial' && isTrialExpired()) {
            return (
                <>
                    Your trial has ended. Please <Link 
                        to="/pricing" 
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to continue using this feature.
                </>
            );
        } else {
            return `You've reached your daily limit. Upgrade for more access or come back tomorrow.`;
        }
    };

    // Define reload limits based on user plan
    const getPlanLoadLimit = () => {
        switch(userPlan) {
            case 'premium':
                return 15;
            case 'pro':
                return 10;
            case 'trial':
            default:
                return 5;
        }
    };

    // Define MAX_RELOAD_COUNT based on user's plan
    const MAX_RELOAD_COUNT = getPlanLoadLimit();
    
    // Debug state for showing plan and limit info
    const [showDebugInfo, setShowDebugInfo] = useState('disabled');

    // NEW: openDropdown state for controlling the accordion/dropdown menus.
    const [openDropdown, setOpenDropdown] = useState(null);

    // Fetch settings from the API when authenticated and whenever refreshSettings changes.
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchSettings = async () => {
            try {
                // Add a global throttle - ensure no more than one call every 2 seconds
                const now = Date.now();
                if (!window.GLOBAL_LAST_SETTINGS_THROTTLE) {
                    window.GLOBAL_LAST_SETTINGS_THROTTLE = 0;
                }
                
                if (now - window.GLOBAL_LAST_SETTINGS_THROTTLE < 2000) { // 2 seconds
                    logger.debug('Home - Too many settings requests, throttling');
                    
                    // Although we're not fetching new settings, we should still update our local state
                    // with the values from localStorage which should be updated by App.js
                    setSettings({
                        learningLanguage: localStorage.getItem('learningLanguage') || "Spanish",
                        languageLevel: localStorage.getItem('languageLevel') || "Beginner",
                        daysOfReview: Number(localStorage.getItem('daysOfReview')) || 3,
                        repeatPercentage: Number(localStorage.getItem('repeatPercentage')) || 0,
                        dailyStoryRepeatNumber: Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0,
                        spanishRegion: localStorage.getItem('spanishRegion') || "Latin America"
                    });
                    return;
                }
                // Update throttle timestamp
                window.GLOBAL_LAST_SETTINGS_THROTTLE = now;
                
                // Check if we've recently fetched settings in App.js (within the last 20 seconds)
                if (window.lastSettingsFetchTime && (now - window.lastSettingsFetchTime < 20000)) {
                    logger.debug('Home - Using cached settings (fetched in the last 20s)');
                    
                    // Although we're not fetching new settings, we should still update our local state
                    // with the values from localStorage which should be updated by App.js
                    setSettings({
                        learningLanguage: localStorage.getItem('learningLanguage') || "Spanish",
                        languageLevel: localStorage.getItem('languageLevel') || "Beginner",
                        daysOfReview: Number(localStorage.getItem('daysOfReview')) || 3,
                        repeatPercentage: Number(localStorage.getItem('repeatPercentage')) || 0,
                        dailyStoryRepeatNumber: Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0,
                        spanishRegion: localStorage.getItem('spanishRegion') || "Latin America"
                    });
                    return;
                }
                
                // Prevent duplicate calls while a fetch is in progress
                if (window.settingsFetchInProgress) {
                    logger.debug('Home - Settings fetch already in progress, skipping duplicate call');
                    return;
                }
                
                window.settingsFetchInProgress = true;
                logger.debug('Home - Fetching settings from API');
                
                const response = await apiClient.get('/api/settings');
                
                // Update the last fetch time and clear the in-progress flag
                window.lastSettingsFetchTime = Date.now();
                window.settingsFetchInProgress = false;
                
                if (!response.ok) throw new Error("Failed to fetch settings");
                const data = await response.json();
                setSettings({
                    learningLanguage: data.learningLanguage || "Spanish",
                    languageLevel: data.languageLevel || "Beginner",
                    daysOfReview: data.daysOfReview || 3,
                    repeatPercentage: data.repeatPercentage || 0,
                    dailyStoryRepeatNumber: data.dailyStoryRepeatNumber || 0,
                    spanishRegion: data.spanishRegion || "Latin America"
                });

                // Also update localStorage for consistency
                localStorage.setItem('learningLanguage', data.learningLanguage || "Spanish");
                localStorage.setItem('languageLevel', data.languageLevel || "Beginner");
                localStorage.setItem('daysOfReview', data.daysOfReview || 3);
                localStorage.setItem('repeatPercentage', data.repeatPercentage || 0);
                localStorage.setItem('dailyStoryRepeatNumber', data.dailyStoryRepeatNumber || 0);
                if (data.learningLanguage === 'Spanish') {
                    localStorage.setItem('spanishRegion', data.spanishRegion || "Latin America");
                }
            } catch (error) {
                // Clear the in-progress flag in case of error
                window.settingsFetchInProgress = false;
                console.error("Error fetching settings in Home:", error);
            }
        };
        fetchSettings();
    }, [isAuthenticated, getToken, refreshSettings]);

    // Persisted settings from backend.
    const learningLanguage = settings.learningLanguage;
    const languageLevelSetting = settings.languageLevel;
    const desiredCount = 10;
    const lsKey = (key) => `${key}_${learningLanguage}`;

    // Keep track of the previous language to detect changes
    const [previousLanguage, setPreviousLanguage] = useState(learningLanguage);

    // Shared Topics & Custom Topic state.
    const [selectedTopics, setSelectedTopics] = useState(() => {
        const stored = localStorage.getItem(lsKey('selectedTopics'));
        return stored ? JSON.parse(stored) : [];
    });
    const [customTopicEnabled, setCustomTopicEnabled] = useState(() => {
        const stored = localStorage.getItem(lsKey('customTopicEnabled'));
        return stored ? JSON.parse(stored) : false;
    });
    const [customTopic, setCustomTopic] = useState(() => {
        const stored = localStorage.getItem(lsKey('customTopic'));
        return stored || "";
    });
    const [selectedGeneralTopic, setSelectedGeneralTopic] = useState(() => localStorage.getItem(lsKey('selectedGeneralTopic')) || "");

    useEffect(() => {
        localStorage.setItem(lsKey('selectedTopics'), JSON.stringify(selectedTopics));
    }, [selectedTopics, learningLanguage]);
    useEffect(() => {
        localStorage.setItem(lsKey('customTopicEnabled'), JSON.stringify(customTopicEnabled));
    }, [customTopicEnabled, learningLanguage]);
    useEffect(() => {
        localStorage.setItem(lsKey('customTopic'), customTopic);
    }, [customTopic, learningLanguage]);
    useEffect(() => {
        localStorage.setItem(lsKey('selectedGeneralTopic'), selectedGeneralTopic);
    }, [selectedGeneralTopic, learningLanguage]);

    // For Japanese/Korean: vocabulary and grammar selections.
    const [selectedVocabularyLevels, setSelectedVocabularyLevels] = useState(() => {
        // Get language-specific stored value if available
        const stored = localStorage.getItem(lsKey('selectedVocabularyLevels'));
        
        if (stored) {
            // Parse the stored value
            const parsedLevels = JSON.parse(stored);
            
            // Filter out levels that don't belong to the current language
            if (learningLanguage === "Japanese") {
                return parsedLevels.filter(level => level.startsWith("N"));
            } else if (learningLanguage === "Korean") {
                return parsedLevels.filter(level => level.startsWith("Level"));
            } else if (learningLanguage === "Chinese") {
                return parsedLevels.filter(level => level.startsWith("HSK"));
            }
            return parsedLevels;
        }
        
        // Set language-specific defaults if no stored value
        if (learningLanguage === "Japanese") return ["N5"];
        if (learningLanguage === "Korean") return ["Level 1"];
        if (learningLanguage === "Chinese") return ["HSK1"];
        return [];
    });
    
    const [selectedGrammarItems, setSelectedGrammarItems] = useState(() => {
        const stored = localStorage.getItem(lsKey('selectedGrammarItems'));
        return stored ? JSON.parse(stored) : {};
    });

    // For non-Japanese (Verbs mode): tenses and endings selections.
    const [selectedTenses, setSelectedTenses] = useState(() => {
        const stored = localStorage.getItem(lsKey('selectedTenses'));
        return stored ? JSON.parse(stored) : [];
    });
    const [selectedEndings, setSelectedEndings] = useState(() => {
        const stored = localStorage.getItem(lsKey('selectedEndings'));
        return stored ? JSON.parse(stored) : [];
    });

    // Learning mode state with custom handler
    const [learningMode, setLearningMode] = useState(() => {
        const storedMode = localStorage.getItem(lsKey('learningMode'));
        return storedMode || "Vocabulary";
    });

    // Custom setter for learning mode that resets related settings
    const handleLearningModeChange = (newMode) => {
        logger.debug(`Changing learning mode from ${learningMode} to ${newMode}`);
        if (newMode !== learningMode) {
            console.log(`Changing learning mode from ${learningMode} to ${newMode}`);

            if (learningLanguage === "Japanese") {
                if (newMode === "Vocabulary") {
                    // Clear grammar items when switching to vocabulary mode
                    setSelectedGrammarItems({});
                } else {
                    // Set vocabulary to only N5 when switching to grammar mode
                    setSelectedVocabularyLevels(["N5"]);
                }
            } else if (learningLanguage === "Korean") {
                if (newMode === "Vocabulary") {
                    // Clear grammar items when switching to vocabulary mode
                    setSelectedGrammarItems({});
                } else {
                    // Set vocabulary to only Level 1 when switching to grammar mode
                    setSelectedVocabularyLevels(["Level 1"]);
                }
            } else if (learningLanguage === "Chinese") {
                if (newMode === "Vocabulary") {
                    // Clear grammar items when switching to vocabulary mode
                    setSelectedGrammarItems({});
                } else {
                    // Set vocabulary to a default (adjust if needed, e.g., HSK1?)
                    // For now, let's clear it, assuming default vocab isn't needed for grammar
                    setSelectedVocabularyLevels([]);
                }
            } else {
                if (newMode === "Vocabulary") {
                    setSelectedTenses([]);
                    setSelectedEndings([]);
                }
            }
            setLearningMode(newMode);
        }
    };

    useEffect(() => {
        localStorage.setItem(lsKey('learningMode'), learningMode);
    }, [learningMode, learningLanguage]);

    // Language change detection and cleanup
    useEffect(() => {
        if (previousLanguage !== learningLanguage) {
            logger.debug(`Language changed from ${previousLanguage} to ${learningLanguage}`);
            console.log(`Language changed from ${previousLanguage} to ${learningLanguage}`);

            setLearningMode("Vocabulary");
            localStorage.setItem(lsKey('learningMode'), "Vocabulary");

            setSelectedTopics([]);
            setCustomTopicEnabled(false);
            setCustomTopic("");
            setSelectedGeneralTopic("");
            localStorage.setItem(lsKey('selectedGeneralTopic'), "");
            
            // Complete replacement of vocabulary levels with language-specific defaults
            if (learningLanguage === "Japanese") {
                setSelectedVocabularyLevels(["N5"]);
            } else if (learningLanguage === "Korean") {
                setSelectedVocabularyLevels(["Level 1"]);
            } else if (learningLanguage === "Chinese") {
                // Set default vocabulary level for Chinese (e.g., HSK1?)
                // For now, let's clear it, assuming no default level is needed initially
                setSelectedVocabularyLevels([]);
            } else {
                setSelectedVocabularyLevels([]);
            }
            
            // Clear grammar items entirely when changing languages
            setSelectedGrammarItems({});
            setSelectedTenses([]);
            setSelectedEndings([]);

            setPreviousLanguage(learningLanguage);
        }
    }, [learningLanguage, previousLanguage, lsKey]);

    // Sentence batch state.
    const [sentences, setSentences] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadingBatch, setLoadingBatch] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const loadingMessages = useMemo(() => [
        "Loading sentences...",
        "Just one moment...",
        "Thanks for your patience..."
    ], []);
    const [loadingMessage, setLoadingMessage] = useState(loadingMessages[0]);

    // Reload counter state (max 5 per day per language).
    const [reloadCount, setReloadCount] = useState(0);
    const [maxReloadMessageVisible, setMaxReloadMessageVisible] = useState(false);

    // Fetch reload count from server
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchReloadCount = async () => {
            try {
                const reloadCounts = await userActivityService.getReloadCounts();
                const currentCount = reloadCounts.sentences[learningLanguage] || 0;
                setReloadCount(currentCount);
                setMaxReloadMessageVisible(currentCount >= MAX_RELOAD_COUNT);
            } catch (error) {
                console.error("Error fetching reload count:", error);
            }
        };
        
        fetchReloadCount();
    }, [isAuthenticated, learningLanguage, MAX_RELOAD_COUNT]);

    // Load stored sentences if available from server
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchSentences = async () => {
            try {
                const userActivity = await userActivityService.getUserActivity();
                const storedSentences = userActivity.sentences?.[learningLanguage] || [];
                // First try to get index from server, then fall back to localStorage if available
                let currentIdx = userActivity.currentIndices?.[learningLanguage];
                
                if (currentIdx === undefined || currentIdx === null) {
                    // If not found on server, try to get from localStorage
                    const savedIndex = localStorage.getItem(lsKey('currentIndex'));
                    currentIdx = savedIndex !== null ? parseInt(savedIndex, 10) : 0;
                } else {
                    // Update localStorage with the server index
                    localStorage.setItem(lsKey('currentIndex'), currentIdx);
                }
                
                if (storedSentences.length > 0) {
                    setSentences(storedSentences);
                    setCurrentIndex(currentIdx);
                } else {
                    setSentences([]);
                    setCurrentIndex(0);
                }
            } catch (error) {
                console.error("Error fetching sentences:", error);
                // Try to get current index from localStorage as fallback
                const savedIndex = localStorage.getItem(lsKey('currentIndex'));
                const currentIdx = savedIndex !== null ? parseInt(savedIndex, 10) : 0;
                
                setSentences([]);
                setCurrentIndex(currentIdx);
            }
        };
        
        fetchSentences();
    }, [isAuthenticated, learningLanguage]);

    // Save current index to server when it changes
    useEffect(() => {
        if (!isAuthenticated || sentences.length === 0) return;
        
        const saveCurrentIndex = async () => {
            try {
                await userActivityService.updateCurrentIndex(learningLanguage, currentIndex);
                // Also save to localStorage for offline/navigation persistence
                localStorage.setItem(lsKey('currentIndex'), currentIndex);
            } catch (error) {
                console.error("Error saving current index:", error);
                // Still save to localStorage even if server save fails
                localStorage.setItem(lsKey('currentIndex'), currentIndex);
            }
        };
        
        saveCurrentIndex();
    }, [currentIndex, isAuthenticated, learningLanguage, sentences.length]);

    // Update loading message interval.
    useEffect(() => {
        let timer;
        if (loadingBatch) {
            let idx = 0;
            setLoadingMessage(loadingMessages[idx]);
            timer = setInterval(() => {
                idx = (idx + 1) % loadingMessages.length;
                setLoadingMessage(loadingMessages[idx]);
            }, 10000);
        } else {
            setLoadingMessage("");
        }
        return () => clearInterval(timer);
    }, [loadingBatch, loadingMessages]);

    useEffect(() => {
        localStorage.setItem(lsKey('selectedVocabularyLevels'), JSON.stringify(selectedVocabularyLevels));
    }, [selectedVocabularyLevels, learningLanguage]);
    useEffect(() => {
        localStorage.setItem(lsKey('selectedGrammarItems'), JSON.stringify(selectedGrammarItems));
    }, [selectedGrammarItems, learningLanguage]);
    useEffect(() => {
        localStorage.setItem(lsKey('selectedTenses'), JSON.stringify(selectedTenses));
    }, [selectedTenses, learningLanguage]);
    useEffect(() => {
        localStorage.setItem(lsKey('selectedEndings'), JSON.stringify(selectedEndings));
    }, [selectedEndings, learningLanguage]);

    // --- finalTopics construction for API ---
    // This will be determined inside fetchSentenceBatch or similar,
    // as it depends on the state at the moment of the API call.

    // --- Menu Validation ---
    let isMenuValid = false;
    if (selectedGeneralTopic) { // If a general topic is selected
        isMenuValid = true;
    } else {
        // Logic for specific topics
        const currentSpecificTopics = [...selectedTopics];
        if (customTopicEnabled && customTopic.trim() !== "") {
            if (!currentSpecificTopics.includes(customTopic.trim())) {
                currentSpecificTopics.push(customTopic.trim());
            }
        }

        if (currentSpecificTopics.length > 0) {
            if (learningLanguage === "Japanese" || learningLanguage === "Korean" || learningLanguage === "Chinese") {
                if (learningMode === "Vocabulary") {
                    isMenuValid = selectedVocabularyLevels.length > 0;
                } else { // Grammar mode
                    isMenuValid = Object.values(selectedGrammarItems).flat().length > 0;
                }
            } else { // Spanish, French, Italian
                if (learningMode === "Verbs") {
                    let validTensesListForValidation = [];
                    if (learningLanguage === "Spanish") validTensesListForValidation = Object.values(spanishVerbTensesList).flat();
                    else if (learningLanguage === "Italian") validTensesListForValidation = Object.values(italianVerbTensesList).flat();
                    else if (learningLanguage === "French") validTensesListForValidation = Object.values(frenchVerbTensesList).flat();

                    isMenuValid =
                        selectedTenses.length > 0 &&
                        selectedTenses.every(t => validTensesListForValidation.includes(t)) &&
                        selectedEndings.length > 0;
                } else { // Vocabulary mode for these languages
                    isMenuValid = true;
                }
            }
        }
    }
    // Debug logs for menu selection state
    // Note: finalTopics for API might differ from currentSpecificTopics if general topic is selected
    logger.debugComponent('Home', "selectedGeneralTopic", selectedGeneralTopic);
    logger.debugComponent('Home', "selectedTopics (specific)", selectedTopics);
    logger.debugComponent('Home', "customTopicEnabled", customTopicEnabled);
    logger.debugComponent('Home', "customTopic", customTopic);
    logger.debugComponent('Home', "learningLanguage", learningLanguage);
    logger.debugComponent('Home', "learningMode", learningMode);
    logger.debugComponent('Home', "selectedVocabularyLevels", selectedVocabularyLevels);
    logger.debugComponent('Home', "selectedGrammarItems", Object.values(selectedGrammarItems).flat());
    logger.debugComponent('Home', "selectedTenses", selectedTenses);
    logger.debugComponent('Home', "selectedEndings", selectedEndings);
    logger.debugComponent('Home', "isMenuValid", isMenuValid);

    const fetchSentenceBatch = async (targetCount) => {
        setLoadingBatch(true);
        setErrorMessage(null);

        // Construct finalTopics based on current state
        let finalTopicsForAPI = [];
        if (selectedGeneralTopic) {
            finalTopicsForAPI = [selectedGeneralTopic];
        } else {
            finalTopicsForAPI = [...selectedTopics];
            if (customTopicEnabled && customTopic.trim() !== "") {
                if (!finalTopicsForAPI.includes(customTopic.trim())) {
                    finalTopicsForAPI.push(customTopic.trim());
                }
            }
        }
        logger.debugComponent('Home', "finalTopics for API", finalTopicsForAPI);


        try {
            const storedLearningMode = localStorage.getItem(lsKey('learningMode')) || 'Vocabulary';
            const repeatPercentage = settings.repeatPercentage;
            logger.debug("Using repeatPercentage:", repeatPercentage);

            // Determine vocabulary levels based on selected grammar items for Asian languages
            let vocabLevels = selectedVocabularyLevels;
            
            if ((learningLanguage === "Japanese" || learningLanguage === "Korean" || learningLanguage === "Chinese") && learningMode === "Grammar" && !selectedGeneralTopic) { // Vocab levels only relevant if not a general topic
                // Get all selected grammar items
                const allSelectedGrammar = Object.values(selectedGrammarItems).flat();
                
                if (allSelectedGrammar.length > 0) {
                    // Extract levels from selected grammar items
                    const grammarLevels = new Set();
                    
                    if (learningLanguage === "Japanese") {
                        // For Japanese, iterate through the japaneseGrammarItems object
                        for (const level in japaneseGrammarItems) {
                            // Check if any selected grammar is in this level
                            const hasGrammarFromLevel = allSelectedGrammar.some(selectedItem => 
                                japaneseGrammarItems[level].includes(selectedItem));
                            
                            if (hasGrammarFromLevel) {
                                grammarLevels.add(level);
                            }
                        }
                    } else if (learningLanguage === "Korean") {
                        // For Korean, iterate through the koreanGrammarItems object
                        for (const level in koreanGrammarItems) {
                            // Check if any selected grammar is in this level
                            const hasGrammarFromLevel = allSelectedGrammar.some(selectedItem => 
                                koreanGrammarItems[level].includes(selectedItem));
                            
                            if (hasGrammarFromLevel) {
                                // Convert from "Topik X (Level Y)" to "Level Y"
                                const match = level.match(/Level \d+/);
                                if (match) {
                                    grammarLevels.add(match[0]);
                                }
                            }
                        }
                    } else if (learningLanguage === "Chinese") {
                        // For Chinese, iterate through the chineseGrammarItems object
                        for (const level in chineseGrammarItems) {
                            // Check if any selected grammar is in this level
                            const hasGrammarFromLevel = allSelectedGrammar.some(selectedItem => 
                                chineseGrammarItems[level].includes(selectedItem));
                            
                            if (hasGrammarFromLevel) {
                                grammarLevels.add(level);
                            }
                        }
                    }
                    
                    // If we found grammar levels, use them for vocabulary
                    if (grammarLevels.size > 0) {
                        vocabLevels = Array.from(grammarLevels);
                        logger.debug(`Using grammar-based vocabulary levels: ${vocabLevels.join(', ')} for ${allSelectedGrammar.length} grammar items`);
                    }
                }
            }

            const payload = (learningLanguage === "Japanese" || learningLanguage === "Korean" || learningLanguage === "Chinese")
                ? {
                    sentenceCount: targetCount,
                    languageLevel: languageLevelSetting,
                    learningLanguage,
                    topics: finalTopicsForAPI,
                    vocabularyLevels: vocabLevels,
                    learningMode,
                    repeatPercentage: repeatPercentage,
                    ...(learningMode === "Grammar" ? { grammarItems: Object.values(selectedGrammarItems).flat() } : {})
                }
                : {
                    sentenceCount: targetCount,
                    languageLevel: languageLevelSetting,
                    learningLanguage,
                    topics: finalTopicsForAPI,
                    tenses: selectedTenses,
                    endings: selectedEndings,
                    learningMode: storedLearningMode,
                    repeatPercentage: repeatPercentage
                };

            if (learningLanguage === "Spanish") {
                payload.spanishRegion = settings.spanishRegion;
            }
            
            if (learningLanguage === "Chinese") {
                payload.chineseCharacterType = localStorage.getItem('chineseCharacterType') || 'Simplified';
            }

            logger.debug("Fetching sentence batch with payload:", payload);

            const data = await apiClient.post('/api/generateSentence', payload);
            
            try {
                // Store sentences on the server and update reload count
                const result = await userActivityService.reloadSentences(learningLanguage, data.sentences);
                
                // If we got a successful response with sentences
                if (result.sentences) {
                    // Update local state with the server response
                    setSentences(result.sentences);
                    setCurrentIndex(result.currentIndex);
                    setReloadCount(result.reloadCount);
                    
                    if (result.reloadCount >= MAX_RELOAD_COUNT) {
                        setMaxReloadMessageVisible(true);
                    }
                } else if (result.error && result.error.includes('Max reload limit')) {
                    // Handle the case where we've hit the max reload limit
                    logger.debug('Max reload limit reached:', result);
                    setMaxReloadMessageVisible(true);
                    setReloadCount(result.currentCount || MAX_RELOAD_COUNT + 1); // Set to max if we don't have the actual count
                }
                // Clear loading state after successful API call
                setLoadingBatch(false);
            } catch (apiError) {
                console.error('Error from reloadSentences API:', apiError);
                
                // If we get a 403 error (max reloads reached), show the message but don't crash
                if (apiError.message && apiError.message.includes('403')) {
                    setMaxReloadMessageVisible(true);
                    setReloadCount(MAX_RELOAD_COUNT + 1); // Assume we've hit the max
                } else {
                    throw apiError; // Re-throw other errors
                }
            }
        } catch (error) {
            console.error("Error fetching sentence batch:", error);
            setErrorMessage(`Error loading: ${error.message}. Please try logging out and back in.`);
            setLoadingMessage("There was a problem loading your sentences. Please try again.");
            setLoadingBatch(false);
        }
    };

    const handleNext = () => {
        if (currentIndex < desiredCount - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            // Immediately save to localStorage for snappier persistence
            localStorage.setItem(lsKey('currentIndex'), newIndex);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            // Immediately save to localStorage for snappier persistence
            localStorage.setItem(lsKey('currentIndex'), newIndex);
        }
    };

    let counterText = "";
    if (sentences.length > 0) {
        counterText = `${currentIndex + 1}/${desiredCount}`;
        if (currentIndex === desiredCount - 1) {
            counterText = "You have completed your sentences for today!";
        }
    }

    const handleLoadButtonClick = async (event) => {
        // If trial has expired, show message and don't allow loading
        if (isTrialExpired()) {
            return;
        }

        if (reloadCount < MAX_RELOAD_COUNT) {
            if (maxReloadMessageVisible) setMaxReloadMessageVisible(false);
            fetchSentenceBatch(desiredCount);
        } else {
            setMaxReloadMessageVisible(true);
        }

        if (reloadCount >= MAX_RELOAD_COUNT) {
            const message = getAccessLimitMessage();
            
            setErrorMessage(message);
            setMaxReloadMessageVisible(true);
            setTimeout(() => {
                setMaxReloadMessageVisible(false);
            }, 5000);
            return;
        }
    };

    // Tips feature state
    const [tipsRead, setTipsRead] = useState(localStorage.getItem("homeTipsRead") === "true");
    const [isClosing, setIsClosing] = useState(false);

    // Handle tips popup open/close
    const handleTipsClick = () => {
        setShowTips(true);
    };

    // Handle tips popup dismissal
    const handleTipsDismiss = () => {
        setIsClosing(true);
        // Delay the actual closing to allow for the animation to play
        setTimeout(() => {
            setShowTips(false);
            setTipsRead(true);
            setIsClosing(false);
            localStorage.setItem("homeTipsRead", "true");
        }, 300); // Match this to the animation duration
    };

    // State for mobile menu overlays
    const [activeOverlay, setActiveOverlay] = useState(null);
    
    // Check if any options are selected for a menu to determine active state
    const isMenuActive = (menuType) => {
        switch (menuType) {
            case 'learningMode':
                return true; // Always has a selection
            case 'topics':
                return selectedGeneralTopic !== "" || selectedTopics.length > 0 || (customTopicEnabled && customTopic.trim() !== "");
            case 'tenses':
                return selectedTenses.length > 0;
            case 'endings':
                return selectedEndings.length > 0;
            case 'vocabularyLevels':
                return selectedVocabularyLevels.length > 0;
            case 'grammarItems':
                return Object.values(selectedGrammarItems).flat().length > 0;
            default:
                return false;
        }
    };

    // Generate tooltip message for disabled load button
    const getLoadButtonTooltip = () => {
        // If trial expired, max reload count reached, or a general topic is selected, don't show specific selection tooltip
        if (isTrialExpired() || reloadCount >= MAX_RELOAD_COUNT || loadingBatch || selectedGeneralTopic) {
            return null;
        }

        // Check which menu items need to be selected for specific topics
        const missingSelections = [];
        
        // Check if specific topics (list or custom) are selected
        const specificTopicsSelected = selectedTopics.length > 0 || (customTopicEnabled && customTopic.trim() !== "");
        if (!specificTopicsSelected) {
            missingSelections.push("topics");
        }
        
        if (learningMode === "Verbs" && !["Japanese", "Korean", "Chinese"].includes(learningLanguage)) {
            if (selectedTenses.length === 0) { // Direct check instead of isMenuActive
                missingSelections.push("verb tenses");
            }
            if (selectedEndings.length === 0) { // Direct check
                missingSelections.push("verb endings");
            }
        }
        
        if (["Japanese", "Korean", "Chinese"].includes(learningLanguage)) {
            if (learningMode === "Vocabulary" && selectedVocabularyLevels.length === 0) { // Direct check
                missingSelections.push("vocabulary levels");
            }
            if (learningMode === "Grammar" && Object.values(selectedGrammarItems).flat().length === 0) { // Direct check
                missingSelections.push("grammar items");
            }
        }
        
        if (missingSelections.length === 0) {
            return ""; // Should not happen if specificTopicsSelected was false, but as a fallback.
        }
        
        if (missingSelections.length === 1) {
            return `Please select ${missingSelections[0]} before loading sentences.`;
        }
        
        const lastItem = missingSelections.pop();
        return `Please select ${missingSelections.join(", ")} and ${lastItem} before loading sentences.`;
    };

    return (
        <div className="main-content">
            <h1 className="page-title">Today's {learningLanguage} Sentences</h1>
            
            {/* Horizontal Menu Bar */}
            <div className="menu-bar">
                {/* Learning Mode button - Always present */}
                <button 
                    className={`home-menu-button ${isMenuActive('learningMode') ? 'active' : ''}`}
                    onClick={() => setActiveOverlay('learningMode')}
                >
                    Mode
                </button>
                
                {/* Topics button - Always present */}
                <button 
                    className={`home-menu-button ${isMenuActive('topics') ? 'active' : ''}`}
                    onClick={() => setActiveOverlay('topics')}
                >
                    Topics
                </button>
                
                {/* Conditional buttons based on learning language and mode */}
                {learningMode === "Verbs" && !["Japanese", "Korean", "Chinese"].includes(learningLanguage) && (
                    <>
                        <button 
                            className={`home-menu-button ${isMenuActive('tenses') ? 'active' : ''}`}
                            onClick={() => setActiveOverlay('tenses')}
                        >
                            Tenses
                        </button>
                        <button 
                            className={`home-menu-button ${isMenuActive('endings') ? 'active' : ''}`}
                            onClick={() => setActiveOverlay('endings')}
                        >
                            Endings
                        </button>
                    </>
                )}
                
                {/* Vocabulary levels button for Japanese, Korean, Chinese */}
                {["Japanese", "Korean", "Chinese"].includes(learningLanguage) && learningMode === "Vocabulary" && (
                        <button
                        className={`home-menu-button ${isMenuActive('vocabularyLevels') ? 'active' : ''}`}
                        onClick={() => setActiveOverlay('vocabularyLevels')}
                        >
                        Levels
                        </button>
                )}
                
                {/* Grammar items button for Japanese, Korean, Chinese */}
                {["Japanese", "Korean", "Chinese"].includes(learningLanguage) && learningMode === "Grammar" && (
                    <button 
                        className={`home-menu-button ${isMenuActive('grammarItems') ? 'active' : ''}`}
                        onClick={() => setActiveOverlay('grammarItems')}
                    >
                        Grammar
                    </button>
                    )}
                </div>

            {/* Content Area */}
            <div className="content-area">
                        {loadingBatch ? (
                    <div className="loading-sentences">{loadingMessage}</div>
                        ) : errorMessage ? (
                    <div>
                        <div className="loading-sentences">{errorMessage}</div>
                                <button
                                    onClick={handleLoadButtonClick}
                                    className="home-load-button"
                                    disabled={isTrialExpired() || reloadCount >= MAX_RELOAD_COUNT || !isMenuValid || loadingBatch}
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : sentences.length === 0 ? (
                    <div className="loading-sentences">No sentences loaded.</div>
                        ) : (
                            <Card
                                sentenceData={sentences[currentIndex]}
                                learningLanguage={settings.learningLanguage}
                                spanishRegion={settings.spanishRegion}
                                onSave={(updatedSentence) => {
                                    const newSentences = [...sentences];
                                    newSentences[currentIndex] = updatedSentence;
                                    setSentences(newSentences);
                                    
                                    // Update sentences in the database through our service
                                    if (isAuthenticated) {
                                        userActivityService.reloadSentences(learningLanguage, newSentences)
                                            .then(() => {
                                                logger.debug("Saved Sentence to server:", updatedSentence);
                                            })
                                            .catch(error => {
                                                console.error("Error saving sentence to server:", error);
                                            });
                                    }
                                }}
                            />
                        )}
                
                    {sentences.length > 0 && !loadingBatch && (
                        <div className="home-counter">
                            <button
                                onClick={handlePrevious}
                                className="home-nav-button"
                                disabled={currentIndex === 0}
                            >
                                Previous
                            </button>
                            <span className="home-counter-text">{counterText}</span>
                            <button
                                onClick={handleNext}
                                className="home-nav-button"
                                disabled={currentIndex >= desiredCount - 1}
                            >
                                Next
                            </button>
                        </div>
                    )}
                    
                    {/* Trial expired and max reload messages */}
                    {isTrialExpired() && (
                        <div className="error-message" style={{ 
                            marginTop: '15px', 
                            textAlign: 'center',
                            color: '#c41c1c',
                            fontSize: '1rem',
                            fontWeight: '500'
                        }}>
                            <span>{getAccessLimitMessage()}</span>
                        </div>
                    )}
                    
                    {maxReloadMessageVisible && !isTrialExpired() && (
                        <div className="error-message" style={{ 
                            marginTop: '15px', 
                            textAlign: 'center',
                            color: '#c41c1c',
                            fontSize: '1rem',
                            fontWeight: '500'
                        }}>
                            {getAccessLimitMessage()}
                        </div>
                    )}
            </div>
            
            {/* Bottom Bar with Load Button */}
            <div className="bottom-bar">
                <div className="home-load-button-container">
                    <button
                        className="home-load-button"
                        onClick={handleLoadButtonClick}
                        disabled={isTrialExpired() || reloadCount >= MAX_RELOAD_COUNT || !isMenuValid || loadingBatch}
                        {...(!loadingBatch && !isMenuValid && getLoadButtonTooltip() ? {
                            title: getLoadButtonTooltip(),
                            'data-tooltip': getLoadButtonTooltip()
                        } : {})}
                    >
                        {loadingBatch ? "Loading..." : sentences.length > 0 ? "Load More Sentences" : "Load Sentences"}
                    </button>
                </div>
            </div>

            {/* Tips Feature */}
            <div className="tips-container">
                <img 
                    src={tipsRead ? tipsOffIcon : tipsOnIcon} 
                    alt="Tips" 
                    style={{ 
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        filter: tipsRead 
                            ? 'grayscale(100%)' 
                            : 'brightness(1.2) sepia(1) hue-rotate(-10deg) saturate(5) drop-shadow(0 0 3px #ffcc00)'
                    }}
                    onClick={handleTipsClick}
                />
                
                {!tipsRead && (
                    <div 
                        className="tips-popup-readme"
                        onClick={handleTipsClick}
                    >
                        Read me first!
                    </div>
                )}
            </div>

            {/* Menu Overlays */}
            {activeOverlay === 'learningMode' && (
                <div className="menu-overlay">
                    <div className="overlay-header">
                        <h3 className="overlay-title">Select Learning Mode</h3>
                        <button className="close-overlay" onClick={() => setActiveOverlay(null)}>✕</button>
                    </div>
                    <div className="menu-options">
                        <label className="option-label">
                            <input
                                type="radio"
                                name="learningMode"
                                value="Vocabulary"
                                checked={learningMode === "Vocabulary"}
                                onChange={(e) => {
                                    handleLearningModeChange(e.target.value);
                                    setActiveOverlay(null);
                                }}
                            />
                            Vocabulary
                        </label>
                        {["Japanese", "Korean", "Chinese"].includes(learningLanguage) ? (
                            <label className="option-label">
                                <input
                                    type="radio"
                                    name="learningMode"
                                    value="Grammar"
                                    checked={learningMode === "Grammar"}
                                    onChange={(e) => {
                                        handleLearningModeChange(e.target.value);
                                        setActiveOverlay(null);
                                    }}
                                />
                                Grammar
                            </label>
                        ) : (
                            <label className="option-label">
                                <input
                                    type="radio"
                                    name="learningMode"
                                    value="Verbs"
                                    checked={learningMode === "Verbs"}
                                    onChange={(e) => {
                                        handleLearningModeChange(e.target.value);
                                        setActiveOverlay(null);
                                    }}
                                />
                                Verbs
                            </label>
                        )}
                    </div>
                </div>
            )}
            
            {activeOverlay === 'topics' && (
                <div className="menu-overlay">
                    <div className="overlay-header">
                        <h3 className="overlay-title">Select Topics</h3>
                        <button className="close-overlay" onClick={() => setActiveOverlay(null)}>✕</button>
                    </div>
                    {(() => {
                        // Calculate effective selected count for the message display
                        const isCustomActiveForMsg = customTopicEnabled && customTopic.trim() !== "" && !selectedTopics.includes(customTopic.trim());
                        let countForMsg = selectedTopics.length;
                        if (isCustomActiveForMsg) {
                            countForMsg++;
                        }
                        if (countForMsg >= 5) {
                            return (
                                <div className="topic-limit-message">
                                    Maximum of 5 specific topics can be selected.
                                </div>
                            );
                        }
                        return null;
                    })()}
                    <div className="menu-options">
                        {/* General Topics Section */}
                        <div className="topic-section-header">General</div>
                        <label className={`option-label ${selectedGeneralTopic === "Fever Dream" || selectedTopics.length > 0 || (customTopicEnabled && customTopic.trim() !== "") ? 'disabled-topic' : ''}`}>
                            <input
                                type="checkbox"
                                checked={selectedGeneralTopic === "Normal"}
                                disabled={selectedGeneralTopic === "Fever Dream" || selectedTopics.length > 0 || (customTopicEnabled && customTopic.trim() !== "")}
                                onChange={(e) => {
                                    const isChecking = e.target.checked;
                                    setSelectedGeneralTopic(isChecking ? "Normal" : "");
                                    if (isChecking) {
                                        setSelectedTopics([]);
                                        setCustomTopicEnabled(false);
                                        setCustomTopic("");
                                    }
                                }}
                            />
                            Normal
                        </label>
                        <label className={`option-label ${selectedGeneralTopic === "Normal" || selectedTopics.length > 0 || (customTopicEnabled && customTopic.trim() !== "") ? 'disabled-topic' : ''}`}>
                            <input
                                type="checkbox"
                                checked={selectedGeneralTopic === "Fever Dream"}
                                disabled={selectedGeneralTopic === "Normal" || selectedTopics.length > 0 || (customTopicEnabled && customTopic.trim() !== "")}
                                onChange={(e) => {
                                    const isChecking = e.target.checked;
                                    setSelectedGeneralTopic(isChecking ? "Fever Dream" : "");
                                    if (isChecking) {
                                        setSelectedTopics([]);
                                        setCustomTopicEnabled(false);
                                        setCustomTopic("");
                                    }
                                }}
                            />
                            Fever Dream
                        </label>

                        {/* Specific Topics Section */}
                        <div className="topic-section-header">Specific</div>
                        <div style={{ maxHeight: 'calc(70vh - 160px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: '10px' }}> {/* Adjusted maxHeight */}
                            {(() => {
                                const isCustomTopicActiveAndUnique = customTopicEnabled && customTopic.trim() !== "" && !selectedTopics.includes(customTopic.trim());
                                let effectiveSelectedCount = selectedTopics.length;
                                if (isCustomTopicActiveAndUnique) {
                                    effectiveSelectedCount++;
                                }

                                return topicsListShared.map((topic, index) => {
                                    const trulyIsSpecificDisabled = selectedGeneralTopic !== "" || (effectiveSelectedCount >= 5 && !selectedTopics.includes(topic));
                                    return (
                                        <div key={index}>
                                            <label className={`option-label ${trulyIsSpecificDisabled ? 'disabled-topic' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={selectedTopics.includes(topic)}
                                                disabled={trulyIsSpecificDisabled}
                                                onChange={() => {
                                                    if (selectedTopics.includes(topic)) {
                                                        setSelectedTopics(selectedTopics.filter((t) => t !== topic));
                                                        // No need to clear general topic when unchecking a specific one
                                                    } else {
                                                        // Only allow selection if general topic is not set and limit is not reached
                                                        if (selectedGeneralTopic === "" && effectiveSelectedCount < 5) {
                                                            setSelectedTopics([...selectedTopics, topic]);
                                                            setSelectedGeneralTopic(""); // Explicitly clear general topic
                                                        } else if (selectedGeneralTopic !== "") {
                                                            // If a general topic is selected, clear it and then select this specific topic
                                                            setSelectedGeneralTopic("");
                                                            setSelectedTopics([topic]); // Start new selection with this topic
                                                            // Reset custom topic as well if it was part of the general clearing logic
                                                            setCustomTopicEnabled(false);
                                                            setCustomTopic("");
                                                        }
                                                    }
                                                }}
                                            />
                                            {topic}
                                        </label>
                                    </div>
                                );
                               });
                            })()}
                        </div>
                        <div className={`custom-topic-container ${selectedGeneralTopic !== "" || (selectedTopics.length >= 5 && !customTopicEnabled) ? 'disabled-custom-topic-container' : ''}`} style={{ marginTop: '10px' }}>
                            <label className={`option-label ${selectedGeneralTopic !== "" || (selectedTopics.length >= 5 && !customTopicEnabled) ? 'disabled-topic' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={customTopicEnabled}
                                    disabled={selectedGeneralTopic !== "" || (selectedTopics.length >= 5 && !customTopicEnabled)}
                                    onChange={(e) => {
                                        const isChecking = e.target.checked;
                                        if (isChecking) {
                                            if (selectedGeneralTopic === "") {
                                                setCustomTopicEnabled(true);
                                                setSelectedGeneralTopic(""); // Explicitly clear general topic
                                            }
                                            // If general topic is active, this checkbox is disabled, so no "else" needed for isChecking=true
                                        } else {
                                            setCustomTopicEnabled(false);
                                            setCustomTopic("");
                                        }
                                    }}
                                />
                                Custom Topic
                            </label>
                            {customTopicEnabled && (
                                <div style={{ marginTop: '5px', padding: '0 10px' }}>
                                    <input
                                        type="text"
                                        value={customTopic}
                                        disabled={selectedGeneralTopic !== "" || !customTopicEnabled || (customTopicEnabled && selectedTopics.length >= 5 && !selectedTopics.includes(customTopic.trim()))}
                                        onChange={(e) => setCustomTopic(e.target.value)}
                                        placeholder="Enter your custom topic"
                                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                                    />
                                    <div style={{ fontSize: '0.8em', fontStyle: 'italic', marginTop: '5px', color: '#777' }}>
                                        Or select from the topics above.
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                className="home-load-button" 
                                style={{ maxWidth: '200px' }}
                                onClick={() => setActiveOverlay(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {activeOverlay === 'tenses' && (
                <div className="menu-overlay">
                    <div className="overlay-header">
                        <h3 className="overlay-title">Select Verb Tenses</h3>
                        <button className="close-overlay" onClick={() => setActiveOverlay(null)}>✕</button>
                    </div>
                    <div className="menu-options">
                        <div style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: '10px' }}>
                            {Object.entries(
                                learningLanguage === "Spanish" ? spanishVerbTensesList :
                                learningLanguage === "French" ? frenchVerbTensesList :
                                learningLanguage === "Italian" ? italianVerbTensesList : {}
                            ).map(([level, tenses]) => (
                                <div key={level}>
                                    <div style={{ fontWeight: 'bold', margin: '10px 0 5px 0' }}>{level}</div>
                                    {tenses.map((tense, idx) => (
                                        <div key={`${level}-${idx}`}>
                                            <label className="option-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTenses.includes(tense)}
                                                    onChange={() => {
                                                        if (selectedTenses.includes(tense)) {
                                                            setSelectedTenses(selectedTenses.filter((t) => t !== tense));
                                                        } else {
                                                            setSelectedTenses([...selectedTenses, tense]);
                                                        }
                                                    }}
                                                />
                                                {tense}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                className="home-load-button" 
                                style={{ maxWidth: '200px' }}
                                onClick={() => setActiveOverlay(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {activeOverlay === 'endings' && (
                <div className="menu-overlay">
                    <div className="overlay-header">
                        <h3 className="overlay-title">Select Verb Endings</h3>
                        <button className="close-overlay" onClick={() => setActiveOverlay(null)}>✕</button>
                    </div>
                    <div className="menu-options">
                        <div style={{ padding: '10px 0' }}>
                            {(learningLanguage === "Spanish" ? spanishVerbEndingsList :
                              learningLanguage === "French" ? frenchVerbEndingsList :
                              learningLanguage === "Italian" ? italianVerbEndingsList : []
                            ).map((ending) => (
                                <div key={ending}>
                                    <label className="option-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedEndings.includes(ending)}
                                            onChange={() => {
                                                if (selectedEndings.includes(ending)) {
                                                    setSelectedEndings(selectedEndings.filter((e) => e !== ending));
                                                } else {
                                                    setSelectedEndings([...selectedEndings, ending]);
                                                }
                                            }}
                                        />
                                        {ending}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                className="home-load-button" 
                                style={{ maxWidth: '200px' }}
                                onClick={() => setActiveOverlay(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {activeOverlay === 'vocabularyLevels' && (
                <div className="menu-overlay">
                    <div className="overlay-header">
                        <h3 className="overlay-title">Select Vocabulary Levels</h3>
                        <button className="close-overlay" onClick={() => setActiveOverlay(null)}>✕</button>
                    </div>
                    <div className="menu-options">
                        <div style={{ padding: '10px 0' }}>
                            {(learningLanguage === "Japanese" ? japaneseVocabularyLevels :
                              learningLanguage === "Korean" ? koreanVocabularyLevels :
                              learningLanguage === "Chinese" ? chineseVocabularyLevels : []
                            ).map((level) => (
                                <div key={level}>
                                    <label className="option-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedVocabularyLevels.includes(level)}
                                            onChange={() => {
                                                if (selectedVocabularyLevels.includes(level)) {
                                                    setSelectedVocabularyLevels(selectedVocabularyLevels.filter((l) => l !== level));
                                                } else {
                                                    setSelectedVocabularyLevels([...selectedVocabularyLevels, level]);
                                                }
                                            }}
                                        />
                                        {level}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                className="home-load-button" 
                                style={{ maxWidth: '200px' }}
                                onClick={() => setActiveOverlay(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {activeOverlay === 'grammarItems' && (
                <div className="menu-overlay">
                    <div className="overlay-header">
                        <h3 className="overlay-title">Select Grammar Items</h3>
                        <button className="close-overlay" onClick={() => setActiveOverlay(null)}>✕</button>
                    </div>
                    <div className="menu-options">
                        {learningLanguage === "Japanese" && (
                            <div className="grammar-accordions">
                                {Object.keys(japaneseGrammarItems).map((level) => (
                                    <div key={level} className="grammar-accordion">
                                        <div 
                                            className="accordion-header"
                                            onClick={() => {
                                                setOpenDropdown(openDropdown === level ? null : level);
                                            }}
                                            style={{
                                                background: 'transparent',
                                                padding: '20px 15px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '2px',
                                                color: 'white',
                                                width: '100%',
                                                borderRadius: '0',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}
                                        >
                                            {level}
                                            <span style={{ color: 'white' }}>{openDropdown === level ? '▲' : '▼'}</span>
                                        </div>
                                        {openDropdown === level && (
                                            <div 
                                                className="accordion-content"
                                                style={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    padding: '10px 15px',
                                                    maxHeight: '400px',
                                                    overflowY: 'auto',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    marginBottom: '10px',
                                                    borderRadius: '0'
                                                }}
                                            >
                                                <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '10px' }}>
                                                    {japaneseGrammarItems[level].map((item, idx) => {
                                                        const isChecked = (selectedGrammarItems[level] || []).includes(item);
                                                        return (
                                                            <div key={idx}>
                                                                <label className="home-menu-label option-label" style={{ margin: '6px 0', fontSize: '14px', display: 'flex', alignItems: 'flex-start' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => {
                                                                            const current = selectedGrammarItems[level] || [];
                                                                            let newSelection;
                                                                            if (isChecked) {
                                                                                newSelection = current.filter((i) => i !== item);
                                                                            } else {
                                                                                newSelection = [...current, item];
                                                                            }
                                                                            setSelectedGrammarItems((prev) => ({
                                                                                ...prev,
                                                                                [level]: newSelection,
                                                                            }));
                                                                        }}
                                                                        style={{ marginRight: '8px', marginTop: '3px' }}
                                                                    />
                                                                    <span>{item}</span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {learningLanguage === "Korean" && (
                            <div className="grammar-accordions">
                                {Object.keys(koreanGrammarItems).map((level) => (
                                    <div key={level} className="grammar-accordion">
                                        <div 
                                            className="accordion-header"
                                            onClick={() => {
                                                setOpenDropdown(openDropdown === level ? null : level);
                                            }}
                                            style={{
                                                background: 'transparent',
                                                padding: '20px 15px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '2px',
                                                color: 'white',
                                                width: '100%',
                                                borderRadius: '0',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}
                                        >
                                            {level}
                                            <span style={{ color: 'white' }}>{openDropdown === level ? '▲' : '▼'}</span>
                                        </div>
                                        {openDropdown === level && (
                                            <div 
                                                className="accordion-content"
                                                style={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    padding: '10px 15px',
                                                    maxHeight: '400px',
                                                    overflowY: 'auto',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    marginBottom: '10px',
                                                    borderRadius: '0'
                                                }}
                                            >
                                                <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '10px' }}>
                                                    {koreanGrammarItems[level].map((item, idx) => {
                                                        const isChecked = (selectedGrammarItems[level] || []).includes(item);
                                                        // Extract just the part before the colon for display
                                                        const displayText = item.split(':')[0];
                                                        return (
                                                            <div key={idx}>
                                                                <label className="home-menu-label option-label" style={{ margin: '6px 0', fontSize: '14px', display: 'flex', alignItems: 'flex-start' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => {
                                                                            const current = selectedGrammarItems[level] || [];
                                                                            let newSelection;
                                                                            if (isChecked) {
                                                                                newSelection = current.filter((i) => i !== item);
                                                                            } else {
                                                                                newSelection = [...current, item];
                                                                            }
                                                                            setSelectedGrammarItems((prev) => ({
                                                                                ...prev,
                                                                                [level]: newSelection,
                                                                            }));
                                                                        }}
                                                                        style={{ marginRight: '8px', marginTop: '3px' }}
                                                                    />
                                                                    <span>{displayText}</span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {learningLanguage === "Chinese" && (
                            <div className="grammar-accordions">
                                {Object.keys(chineseGrammarItems).map((level) => (
                                    <div key={level} className="grammar-accordion">
                                        <div 
                                            className="accordion-header"
                                            onClick={() => {
                                                setOpenDropdown(openDropdown === level ? null : level);
                                            }}
                                            style={{
                                                background: 'transparent',
                                                padding: '20px 15px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '2px',
                                                color: 'white',
                                                width: '100%',
                                                borderRadius: '0',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}
                                        >
                                            {level}
                                            <span style={{ color: 'white' }}>{openDropdown === level ? '▲' : '▼'}</span>
                                        </div>
                                        {openDropdown === level && (
                                            <div 
                                                className="accordion-content"
                                                style={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    padding: '10px 15px',
                                                    maxHeight: '400px',
                                                    overflowY: 'auto',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    marginBottom: '10px',
                                                    borderRadius: '0'
                                                }}
                                            >
                                                <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '10px' }}>
                                                    {chineseGrammarItems[level].map((item, idx) => {
                                                        const isChecked = (selectedGrammarItems[level] || []).includes(item);
                                                        // Extract just the part before the colon for display
                                                        const displayText = item.split(':')[0];
                                                        return (
                                                            <div key={idx}>
                                                                <label className="home-menu-label option-label" style={{ margin: '6px 0', fontSize: '14px', display: 'flex', alignItems: 'flex-start' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => {
                                                                            const current = selectedGrammarItems[level] || [];
                                                                            let newSelection;
                                                                            if (isChecked) {
                                                                                newSelection = current.filter((i) => i !== item);
                                                                            } else {
                                                                                newSelection = [...current, item];
                                                                            }
                                                                            setSelectedGrammarItems((prev) => ({
                                                                                ...prev,
                                                                                [level]: newSelection,
                                                                            }));
                                                                        }}
                                                                        style={{ marginRight: '8px', marginTop: '3px' }}
                                                                    />
                                                                    <span>{displayText}</span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                className="home-load-button" 
                                style={{ maxWidth: '200px' }}
                                onClick={() => setActiveOverlay(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Tips Popup - keeping the existing code with some minor adjustments */}
            {showTips && (
                <div className="tips-popup-overlay" style={{
                    animation: isClosing ? 'fadeOut 0.3s ease-in-out' : 'fadeIn 0.3s ease-in-out'
                }}>
                    <div className="tips-popup" style={{
                        animation: isClosing ? 'scaleOut 0.3s ease-in-out' : 'scaleIn 0.3s ease-in-out'
                    }}>
                        <h2 className="tips-popup-title">Tips:</h2>
                        <hr className="tips-popup-divider" />
                        <div className="tips-popup-content">
                            <ul className="tips-popup-list">
                                <li>If the sentences load improperly, simply try reloading them again.</li>
                                <li>
                                    For better results with studying verbs, it's recommended to choose one verb tense and one verb ending at a time to add to your playlist. Spend time mastering each one before proceeding.
                                </li>
                                <li>
                                    For higher quality sentences, it's better to choose a few different topics at a time. Recommended: 3 - 4.
                                </li>
                                <li>
                                    Feel free to skip adding certain sentences to your playlist if you don't like them, and then reload more to complete your daily playlist. Pro members can load up to 15 times, and Trial users can load up to 5.
                                </li>
                            </ul>
                        </div>
                        <div className="tips-popup-button-container">
                            <button 
                                onClick={handleTipsDismiss} 
                                className="tips-popup-dismiss-button"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
